import { useApp } from '@/contexts/AppContext';
import { ReactNode } from 'react';
import { NotificationsSheet } from './NotificationsSheet';
import { cn } from '@/lib/utils';
import { Button } from './ui/button';
import { LogOut } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

interface HeaderProps {
  title: string;
  subtitle?: string;
  showWelcome?: boolean;
  action?: ReactNode;
}

export function Header({ title, subtitle, showWelcome = false, action }: HeaderProps) {
  const { user, setUser, unreadNotificationsCount } = useApp();
  const navigate = useNavigate();

  // Get username or fallback to email
  const displayName = user?.user_metadata?.username || 
                     user?.email?.split('@')[0] || 
                     'there';

  const handleSignOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        toast({
          title: 'Error',
          description: error.message,
          variant: 'destructive',
        });
      } else {
        setUser(null);
        navigate('/auth');
      }
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="flex h-12 items-center px-4">
          <div className="flex items-center gap-2">
            <a href="/" className="flex items-center">
              <span className="font-bold text-lg">AI-Tasky</span>
            </a>
          </div>
          <div className="flex flex-1 items-center justify-between md:justify-end">
            <div className="flex-1 md:flex-none">
              {showWelcome && (
                <h1 className="text-base font-medium ml-4">
                  Welcome back, {displayName}!
                </h1>
              )}
            </div>
            <div className="flex items-center gap-2">
              <NotificationsSheet unreadCount={unreadNotificationsCount} />
              <Button
                variant="ghost"
                size="icon"
                onClick={handleSignOut}
                className="h-8 w-8"
              >
                <LogOut className="h-4 w-4" />
              </Button>
              {action}
            </div>
          </div>
        </div>
      </header>
      {/* Add a spacer to prevent content from going under the fixed header */}
      <div className="h-12" />
    </>
  );
} 